import { HeartHandshake, Briefcase, Car, Check } from "lucide-react";
import { motion } from "framer-motion";

const services = [
  {
    title: "Transport Médical (VSL)",
    description:
      "Conventionné CPAM pour vos rendez-vous médicaux, hospitalisations, dialyses et consultations spécialisées.",
    icon: HeartHandshake,
    image:
      "https://images.unsplash.com/photo-1764173039712-da4632d1fa9d?w=600&q=80",
    color: "text-emerald-400",
    borderColor: "border-emerald-500/30",
    hoverBorder: "hover:border-emerald-500/60",
    gradient: "from-emerald-500/20",
    features: [
      "Agréé CPAM",
      "Aide administrative",
      "Accompagnement en service",
      "Hygiène stricte",
    ],
  },
  {
    title: "Courses Professionnelles",
    description:
      "Ponctualité garantie pour vos déplacements d'affaires, transferts gares et aéroports.",
    icon: Briefcase,
    image:
      "https://images.unsplash.com/photo-1630705318048-184e6193d4ae?w=600&q=80",
    color: "text-blue-400",
    borderColor: "border-blue-500/30",
    hoverBorder: "hover:border-blue-500/60",
    gradient: "from-blue-500/20",
    features: [
      "Ponctualité garantie",
      "Confort de travail",
      "Facturation entreprise",
      "Transferts Gares/Aéroports",
    ],
  },
  {
    title: "Courses Privées",
    description:
      "Pour vos déplacements personnels, sorties ou événements. Disponible 24h/24 et 7j/7.",
    icon: Car,
    image:
      "https://images.unsplash.com/photo-1756925974726-442aaa8f0538?w=600&q=80",
    color: "text-amber-400",
    borderColor: "border-amber-500/30",
    hoverBorder: "hover:border-amber-500/60",
    gradient: "from-amber-500/20",
    features: [
      "Disponibilité 24/7",
      "Toutes distances",
      "Siège enfant sur demande",
      "Tarifs transparents",
    ],
  },
];

export default function ServicesSection() {
  return (
    <section className="py-20 md:py-28 bg-secondary/30" data-testid="services-section" id="services" aria-label="Services de transport RSE TAXI">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-amber-400 font-tagline text-sm tracking-[0.3em] uppercase font-semibold">
            Nos expertises
          </span>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mt-3">
            Services de Transport au Nord de Toulouse
          </h2>
          <p className="text-zinc-400 mt-3 text-base md:text-lg max-w-xl mx-auto">
            Transport médical VSL conventionné CPAM, courses professionnelles et privées dans tout le Nord de Toulouse.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {services.map((s, i) => (
            <motion.article
              key={s.title}
              className={`group bg-card border ${s.borderColor} ${s.hoverBorder} rounded-2xl overflow-hidden shadow-xl transition-all duration-500`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              whileHover={{ y: -6, transition: { duration: 0.3 } }}
              data-testid={`service-card-${i}`}
            >
              <div className="h-52 overflow-hidden relative">
                <img
                  src={s.image}
                  alt={s.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className={`absolute inset-0 bg-gradient-to-t ${s.gradient} to-transparent opacity-60`} />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2.5 rounded-xl bg-white/5 ${s.color} border border-white/10`}>
                    <s.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold text-white">{s.title}</h3>
                </div>
                <p className="text-zinc-400 text-sm leading-relaxed mb-5">
                  {s.description}
                </p>
                <ul className="space-y-2.5">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-center gap-2.5 text-sm text-zinc-300">
                      <Check className={`h-4 w-4 ${s.color} flex-shrink-0`} />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
